import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Mail, ArrowRight, Loader2, XCircle, Truck, Clock, Shield, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { trackPurchase } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const purchaseTracked = useRef(false); // Prevent duplicate tracking
  const maxAttempts = 10;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    let currentAttempt = 0;

    const pollPaymentStatus = async () => {
      if (cancelled) return;
      try {
        const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();
        if (cancelled) return;
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          localStorage.removeItem('spa_cart');
          
          // Track GA4 purchase event (only once)
          if (!purchaseTracked.current && data.order) {
            purchaseTracked.current = true;
            trackPurchase({
              order_number: data.order.order_number,
              transaction_id: sessionId,
              total: data.order.total_amount || data.amount_total,
              shipping_cost: data.order.shipping_cost_ttc || 0,
              items: data.order.items || []
            });
          }
          
          return;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return;
        }

        if (currentAttempt < maxAttempts) {
          currentAttempt++;
          setAttempts(currentAttempt);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('success');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (currentAttempt < maxAttempts) {
          currentAttempt++;
          setAttempts(currentAttempt);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <SEO 
          title="Vérification du paiement | Atlantique Spa Boutique"
          description="Vérification de votre paiement en cours..."
        />
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-16 h-16 text-[#1E3A5F] animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">
              Vérification du paiement...
            </h1>
            <p className="text-slate-600">
              Merci de patienter pendant que nous confirmons votre paiement.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (status === 'failed' || status === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <SEO 
          title="Erreur de paiement | Atlantique Spa Boutique"
          description="Une erreur est survenue lors du paiement."
        />
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">
              {status === 'failed' ? 'Paiement non abouti' : 'Erreur de vérification'}
            </h1>
            <p className="text-slate-600 mb-6">
              {status === 'failed' 
                ? 'Votre session de paiement a expiré ou a été annulée.'
                : 'Impossible de vérifier le statut de votre paiement.'
              }
            </p>
            <div className="space-y-3">
              <Link to="/panier">
                <Button className="w-full bg-[#1E3A5F] hover:bg-[#152C4A]">
                  Retour au panier
                </Button>
              </Link>
              <a href="mailto:contact@atlantiquespaboutique.com">
                <Button variant="outline" className="w-full">
                  Nous contacter par e-mail
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state - Professional UX
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 md:py-12 px-4">
      <SEO 
        title="Commande confirmée | Atlantique Spa Boutique"
        description="Votre commande a été confirmée. Merci pour votre achat chez Atlantique Spa Boutique."
      />
      
      <div className="max-w-2xl mx-auto space-y-6">
        
        {/* ========== BLOC 1: Confirmation principale ========== */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-8 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Paiement confirmé – Merci pour votre commande
            </h1>
            <p className="text-emerald-100 text-lg">
              Votre paiement a bien été enregistré.
            </p>
          </div>
          
          <CardContent className="p-6 md:p-8">
            {/* Récapitulatif de commande avec détails complets */}
            {paymentData && (
              <div className="bg-slate-50 rounded-xl p-5 mb-6 border border-slate-100">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Récapitulatif de votre commande
                </h2>
                <div className="space-y-3 text-sm">
                  {/* Numéro de commande */}
                  {paymentData.order?.order_number && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-200">
                      <span className="text-slate-600">N° de commande :</span>
                      <span className="font-bold text-slate-900 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-lg">
                        {paymentData.order.order_number}
                      </span>
                    </div>
                  )}
                  
                  {/* Email client */}
                  {paymentData.order?.customer_email && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-200">
                      <span className="text-slate-600">Email :</span>
                      <span className="text-slate-900">
                        {paymentData.order.customer_email}
                      </span>
                    </div>
                  )}
                  
                  {/* Produits commandés */}
                  {paymentData.order?.items && paymentData.order.items.length > 0 && (
                    <div className="py-3 border-b border-slate-200">
                      <span className="text-slate-600 block mb-2">Produits :</span>
                      <div className="space-y-2 pl-2">
                        {paymentData.order.items.map((item, index) => (
                          <div key={index} className="flex justify-between items-center text-slate-800">
                            <span>{item.name} <span className="text-slate-500">x{item.quantity || 1}</span></span>
                            <span className="font-medium">{(item.price * (item.quantity || 1)).toFixed(2).replace('.', ',')} €</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Sous-total */}
                  {paymentData.order?.subtotal && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-200">
                      <span className="text-slate-600">Sous-total :</span>
                      <span className="text-slate-900">
                        {paymentData.order.subtotal.toFixed(2).replace('.', ',')} € TTC
                      </span>
                    </div>
                  )}
                  
                  {/* Livraison */}
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600">Livraison :</span>
                    {paymentData.order?.shipping_free ? (
                      <span className="text-emerald-600 font-medium">Offerte</span>
                    ) : (
                      <span className="text-slate-900">
                        {(paymentData.order?.shipping_cost_ttc || 0).toFixed(2).replace('.', ',')} € TTC
                      </span>
                    )}
                  </div>
                  
                  {/* Total */}
                  <div className="flex justify-between items-center py-3 bg-slate-100 -mx-5 px-5 mt-3 rounded-b-xl">
                    <span className="font-semibold text-slate-900">Total payé :</span>
                    <span className="font-bold text-xl text-slate-900">
                      {paymentData.amount_total?.toFixed(2).replace('.', ',')} € TTC
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Confirmation email */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100 mb-6">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-800">
                  <strong>Un e-mail de confirmation</strong> a été envoyé à{' '}
                  <span className="font-medium">{paymentData?.order?.customer_email || 'votre adresse'}</span> avec le récapitulatif complet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== BLOC 2: Prochaines étapes ========== */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#1E3A5F]" />
              Que se passe-t-il maintenant ?
            </h2>
            
            <div className="space-y-4">
              {/* Étape 1 */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Votre commande est en cours de préparation</p>
                  <p className="text-sm text-slate-600">Nos équipes ont bien reçu votre commande.</p>
                </div>
              </div>
              
              {/* Étape 2 */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Les produits sont vérifiés et emballés</p>
                  <p className="text-sm text-slate-600">Chaque article est soigneusement contrôlé avant expédition.</p>
                </div>
              </div>
              
              {/* Étape 3 */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Truck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Expédition sous 1 à 3 jours ouvrés</p>
                  <p className="text-sm text-slate-600">Hors week-end et jours fériés. Délai variable selon disponibilité.</p>
                </div>
              </div>
              
              {/* Étape 4 */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Vous recevrez un e-mail dès l'expédition</p>
                  <p className="text-sm text-slate-600">Avec les informations de suivi de votre colis.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== BLOC 3: Service client (EMAIL UNIQUEMENT) ========== */}
        <Card className="border-0 shadow-lg bg-slate-800 text-white">
          <CardContent className="p-6 md:p-8 text-center">
            <Mail className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">
              Une question concernant votre commande ?
            </h2>
            <p className="text-slate-300 mb-4">
              Contactez-nous exclusivement par e-mail :
            </p>
            <a 
              href="mailto:contact@atlantiquespaboutique.com" 
              className="inline-block text-white font-semibold text-lg bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-colors"
            >
              contact@atlantiquespaboutique.com
            </a>
            <p className="text-slate-400 text-sm mt-4">
              Nous vous répondons dans les meilleurs délais.
            </p>
          </CardContent>
        </Card>

        {/* ========== BLOC 4: Rassurance ========== */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-5 shadow-md text-center border border-slate-100">
            <Shield className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
            <p className="font-medium text-slate-900 text-sm">Paiement sécurisé</p>
            <p className="text-xs text-slate-500 mt-1">via Stripe</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md text-center border border-slate-100">
            <Truck className="w-8 h-8 text-blue-600 mx-auto mb-3" />
            <p className="font-medium text-slate-900 text-sm">Livraison France entière</p>
            <p className="text-xs text-slate-500 mt-1">Offerte dès 80 € TTC</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md text-center border border-slate-100">
            <FileText className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="font-medium text-slate-900 text-sm">Facture disponible</p>
            <p className="text-xs text-slate-500 mt-1">sur demande</p>
          </div>
        </div>

        {/* ========== Boutons d'action ========== */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Link to="/" className="flex-1">
            <Button className="w-full bg-[#1E3A5F] hover:bg-[#152C4A] py-6 text-base">
              Retour à l'accueil
            </Button>
          </Link>
          <Link to="/pieces-accessoires" className="flex-1">
            <Button variant="outline" className="w-full py-6 text-base border-slate-300">
              Continuer mes achats
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
